import { ThemeEvents } from '@theme/events';
import { Component } from '@theme/component';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 * @property {HTMLElement} [priceTiers]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  /** @type {AbortController | null} */
  #tierAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    if (this.querySelector('[ref="priceTiers"]')) {
      this.#tierAbortController = new AbortController();
      const { signal } = this.#tierAbortController;
      document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
      document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, { signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#tierAbortController?.abort();
    this.#tierAbortController = null;
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  /**
   * Returns the product form that shares this price block's section or dialog.
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    return this.closest('.shopify-section, dialog')?.querySelector('product-form-component') ?? null;
  }

  /**
   * Marks the pricing tier matching the cart quantity plus the quantity being added,
   * mirroring how price-per-item picks its price.
   */
  #updateActiveTier() {
    const rows = this.querySelectorAll('[ref="priceTiers"] tr[data-min-quantity]');
    if (!rows.length) return;

    const quantityInput = this.#getProductForm()?.querySelector('input[name="quantity"]');
    if (!(quantityInput instanceof HTMLInputElement)) return;

    const cartQuantity = parseInt(quantityInput.dataset.cartQuantity || '0') || 0;
    const quantity = cartQuantity + (parseInt(quantityInput.value) || 1);

    // Rows render in ascending order; the last one the quantity reaches is active, falling back to the first
    let activeRow = rows[0];
    for (const row of rows) {
      if (row instanceof HTMLElement && quantity >= (parseInt(row.dataset.minQuantity || '') || 0)) activeRow = row;
    }

    for (const row of rows) {
      if (row === activeRow) {
        row.setAttribute('aria-current', 'true');
      } else {
        row.removeAttribute('aria-current');
      }
    }
  }

  /**
   * Handles quantity selector updates from this section's product form.
   * @param {Event} event
   */
  #handleQuantityUpdate = (event) => {
    const form = this.#getProductForm();
    if (!form || !(event.target instanceof Node) || !form.contains(event.target)) return;

    this.#updateActiveTier();
  };

  /**
   * Re-evaluates the active tier once the cart response settles.
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    event.promise
      ?.then(() => this.#updateActiveTier())
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-price] Cart update promise rejected:', error);
      });
  };

  /**
   * Handles product select event and updates the price.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const { priceContainer, volumePricingNote } = this.refs;
        // Find the new product-price element in the updated HTML
        const newProductPrice = html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`);
        if (!newProductPrice) return;

        // Update price container
        const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
        if (newPrice && priceContainer) {
          priceContainer.replaceWith(newPrice);
        }

        // Update volume pricing note
        const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');
        if (!newNote) {
          volumePricingNote?.remove();
        } else if (!volumePricingNote) {
          // Use newPrice since priceContainer was just replaced and now points to the detached element
          newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
        } else {
          volumePricingNote.replaceWith(newNote);
        }

        // Update quantity pricing tiers (developer preview, only rendered for tagged customers)
        const priceTiers = this.querySelector('[ref="priceTiers"]');
        const newTiers = newProductPrice.querySelector('[ref="priceTiers"]');
        if (priceTiers && newTiers) {
          priceTiers.replaceWith(newTiers);
          this.#updateActiveTier();
        }

        // Update installments (SPI banner) variant ID to trigger payment terms re-render
        const installmentsInput = /** @type {HTMLInputElement|null} */ (
          this.querySelector(`#product-form-installment-${this.dataset.blockId} input[name="id"]`)
        );
        if (installmentsInput) {
          installmentsInput.value = detail.resource?.id ?? '';
          installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-price] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
